'use client';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { IUserPortalSecretKeys, TGunUserNode } from '@/types';
import { toast } from '@fileverse/ui';

import { useCurrentPrivyWallet } from '@/hooks/useCurrentPrivyWallet';
import { IPortalStorage, usePortalStorage } from '@/hooks/use-portal-storage';
import { WorkerInstance } from '@/utils/worker-utils/worker-instance';
import { IdentityInstance } from '@/utils/identity-utils/identity-contract-module';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';

import { useLogout } from '@/hooks/useLogout';
import { captureException } from '@sentry/nextjs';
import { IdentitySetupModal } from '../identity-setup-modal/identity-setup-modal';
import { Hex, zeroAddress } from 'viem';
import { SetupModalState } from '../identity-setup-modal/types';
import { getIdentitySignedMessage } from '@/db/queries';
import { usePathname } from 'next/navigation';
import { AgentInstance } from '@/utils/identity-utils/smart-account-agent';
import { AccountMigrationSuccessfull } from './account-migration-successfull';
import { ImpossibleMigrationModal } from './impossible-migration-modal';
import { MigrationAlertBanner } from './migration-alert-banner';
import { usePlausibleEvents } from '@/hooks/use-plausible-events';
import { usePrivy } from '@privy-io/react-auth';
import { findConnector } from '@/utils/find-connector';

interface IAccountContext {
  userNode: any;
  isLoading: boolean;
  portalAddress: string | null;
  editSecret: string | null;
  ownerEncryptionKey: string | null;
  portalEncryptionKey: string | null;
  walletAddress: string | undefined;
  storage: IPortalStorage | null;
  isStorageFull: boolean;
  initializeAccount: () => Promise<void>;
  isOpen: boolean;
  identityAlreadyExists: boolean;
  onCompleteSetup: (flow?: 'legacy' | 'new') => void;
  identityInitialized: boolean;
  ownerDecryptionKey: string | null;
}

const AccountContext = createContext<IAccountContext>({
  isLoading: false,
  portalAddress: null,
  editSecret: null,
  ownerEncryptionKey: null,
  portalEncryptionKey: null,
  userNode: null,
  walletAddress: undefined,
  storage: null,
  isStorageFull: false,
  initializeAccount: () => Promise.resolve(),
  isOpen: false,
  identityAlreadyExists: false,
  onCompleteSetup: () => {},
  identityInitialized: false,
  ownerDecryptionKey: null,
});

export const useAccountContext = () => {
  return useContext(AccountContext);
};

export const AccountProvider = memo(
  ({ children }: { children: React.ReactNode }) => {
    const [identityInitialized, setIdentityInitialized] =
      useState<boolean>(false);
    const [portalAddress, setPortalAddress] = useState<string | null>(null);
    const [portalSecretKeys, setPortalSecretKeys] =
      useState<IUserPortalSecretKeys | null>(null);
    const [isAccountMigrationComplete, setIsAccountMigrationComplete] =
      useState<boolean>(false);
    const [impossibleToMigrate, setImpossibleToMigrate] =
      useState<boolean>(false);
    const { onAccountMigrationSuccess: onAccountMigrationSuccessPlausible } =
      usePlausibleEvents();

    const pathname = usePathname();

    const [userNode, setUserNode] = useState<TGunUserNode | null>(null);

    const [setupModalState, setSetupModalState] = useState<SetupModalState>({
      isOpen: false,
      identityAlreadyExists: false,
    });

    const [loading, setLoading] = useState(true);
    const { onLogout } = useLogout();
    const { currentWallet, ready, authenticated } = useCurrentPrivyWallet();
    const { user } = usePrivy();
    const { onLoggedInVisit } = usePlausibleEvents();

    const isViewPage = pathname !== '/' && !pathname.includes('document');

    const resetAccount = useCallback(() => {
      setIdentityInitialized(false);
      setUserNode(null);
      setPortalAddress(null);
      setPortalSecretKeys(null);
      WorkerInstance.dispose();
      AgentInstance.resetAgentClient();
      KeyStoreInstance.resetKeyStore();
      IdentityInstance.resetIdentity();
    }, []);

    const onCompleteSetup = useCallback((flow: 'legacy' | 'new' = 'legacy') => {
      setSetupModalState({
        isOpen: false,
        identityAlreadyExists: false,
      });
      setPortalAddress(KeyStoreInstance.getPortalAddress());

      setPortalSecretKeys({
        editSecret: KeyStoreInstance.getEditSecret(),
        ownerEncryptionKey: KeyStoreInstance.getOwnerPublicKey(),
        portalEncryptionKey: KeyStoreInstance.getPortalPublicKey(),
        ownerDecryptionKey: KeyStoreInstance.getOwnerPrivateKey(),
      });

      const userNode = KeyStoreInstance.getGunUserNode();

      if (userNode) {
        setUserNode(userNode);
      }
      setIdentityInitialized(true);
      setLoading(false);
      if (flow === 'legacy') {
        onAccountMigrationSuccessPlausible();
        setIsAccountMigrationComplete(true);
        if (!userNode) {
          setImpossibleToMigrate(true);
        }
      }
    }, []);

    const initializeAccount = useCallback(async () => {
      if (!ready) return;
      try {
        if (!currentWallet?.address) return;
        const signedMsg = await getIdentitySignedMessage(
          currentWallet?.address as Hex
        );
        if (!signedMsg || !authenticated) return;
        setLoading(true);
        const identityModuleAddress =
          await IdentityInstance.getIdentityModuleAddress(currentWallet);

        if (identityModuleAddress === zeroAddress && !isViewPage) {
          setSetupModalState({
            isOpen: true,
            identityAlreadyExists: false,
          });
          return;
        }

        const { initialized } = await IdentityInstance.initialize(
          identityModuleAddress
        );

        if (!initialized) return;
        const keyStoreData = await KeyStoreInstance.getKeyStore();

        if (!keyStoreData && !isViewPage) {
          setSetupModalState({
            isOpen: true,
            identityAlreadyExists: true,
          });
          return;
        }

        if (!keyStoreData) return;

        await KeyStoreInstance.initializeKeyStore(keyStoreData);

        setPortalAddress(KeyStoreInstance.getPortalAddress());

        setPortalSecretKeys({
          editSecret: KeyStoreInstance.getEditSecret(),
          ownerEncryptionKey: KeyStoreInstance.getOwnerPublicKey(),
          portalEncryptionKey: KeyStoreInstance.getPortalPublicKey(),
          ownerDecryptionKey: KeyStoreInstance.getOwnerPrivateKey(),
        });

        const userNode = KeyStoreInstance.getGunUserNode();
        if (userNode) {
          setUserNode(userNode);
        }
        setIdentityInitialized(true);
      } catch (e) {
        console.log(e);
        authenticated && (await onLogout(false));
        captureException(e, {
          level: 'error',
          extra: {
            message: 'Failed to authenticate',
          },
        });
        toast({
          description: 'Failed to authenticate',
          variant: 'danger',
          hasIcon: true,
        });
      } finally {
        setLoading(false);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, ready, currentWallet?.address, isViewPage]);

    useEffect(() => {
      initializeAccount();

      return () => {
        resetAccount();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initializeAccount, resetAccount]);

    useEffect(() => {
      if (isViewPage && currentWallet?.address && user) {
        const connectionMethod = findConnector({
          farcaster: user?.farcaster?.username as string,
          email: user?.email?.address,
          github: user?.github?.username as string,
          twitter: user?.twitter?.username as string,
          discord: user?.discord?.username as string,
          walletAddress: user?.wallet?.address,
        });
        onLoggedInVisit(connectionMethod?.connectMedium as string);
      }
    }, [isViewPage, currentWallet, user]);

    const {
      editSecret = null,
      ownerEncryptionKey = null,
      portalEncryptionKey = null,
      ownerDecryptionKey = null,
    } = portalSecretKeys || {};

    const { storage } = usePortalStorage(identityInitialized);

    const { isOpen, identityAlreadyExists } = setupModalState;
    return (
      <AccountContext.Provider
        value={{
          isLoading: loading,
          userNode,
          walletAddress: currentWallet?.address,
          storage,
          portalAddress,
          editSecret,
          ownerEncryptionKey,
          portalEncryptionKey,
          isStorageFull: storage.usedStoragePercentage > 99,
          initializeAccount,
          isOpen,
          identityAlreadyExists,
          onCompleteSetup,
          identityInitialized,
          ownerDecryptionKey,
        }}
      >
        {children}
        {isOpen && !isViewPage && <IdentitySetupModal />}

        {isAccountMigrationComplete && (
          <AccountMigrationSuccessfull
            isAccountMigrationComplete={isAccountMigrationComplete}
            setIsAccountMigrationComplete={setIsAccountMigrationComplete}
          />
        )}

        {impossibleToMigrate && (
          <div className="absolute top-0 left-0 w-full z-[999]">
            <ImpossibleMigrationModal
              isImpossibleToMigrate={impossibleToMigrate}
              setIsImpossibleToMigrate={setImpossibleToMigrate}
            />
          </div>
        )}

        <MigrationAlertBanner />
      </AccountContext.Provider>
    );
  }
);

AccountProvider.displayName = 'AccountProvider';
